import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../../api';
import { compressImage } from '../../components/ProfilePrompt';

const LOADING_TEXTS = [
  'Reading facial geometry...',
  'Identifying your pattern...',
  'Cross-referencing your choices...',
  'Almost there...',
];

const DOMINANT_DESCRIPTIONS = {
  vata:  'angular, refined features',
  pitta: 'defined, sharp features',
  kapha: 'warm, soft features',
};

function getTypeDescription(vata, pitta, kapha, dominant) {
  const sorted = [['vata', vata], ['pitta', pitta], ['kapha', kapha]].sort((a, b) => b[1] - a[1]);
  const gap = sorted[0][1] - sorted[1][1];
  if (gap < 15) {
    const nameA = sorted[0][0], nameB = sorted[1][0];
    const mixed = {
      'vata-pitta':  'angular meets defined features',
      'pitta-vata':  'angular meets defined features',
      'pitta-kapha': 'defined meets warm features',
      'kapha-pitta': 'defined meets warm features',
      'vata-kapha':  'angular meets soft features',
      'kapha-vata':  'angular meets soft features',
    };
    return mixed[`${nameA}-${nameB}`] || `a blend of ${nameA} and ${nameB} energy`;
  }
  return DOMINANT_DESCRIPTIONS[dominant] || 'a unique energy signature';
}

export default function AttractionPhotos() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [photos, setPhotos] = useState([]);
  const [phase, setPhase] = useState('upload');
  const [loadingIdx, setLoadingIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!getToken()) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (phase !== 'loading') return;
    const id = setInterval(() => setLoadingIdx(i => (i + 1) % LOADING_TEXTS.length), 2000);
    return () => clearInterval(id);
  }, [phase]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const slots = 5 - photos.length;
    const toProcess = files.slice(0, slots);

    const newPhotos = await Promise.all(toProcess.map(async (file) => {
      const preview = URL.createObjectURL(file);
      let base64;
      try { base64 = await compressImage(file); }
      catch {
        base64 = await new Promise(res => {
          const reader = new FileReader();
          reader.onload = ev => res(ev.target.result);
          reader.readAsDataURL(file);
        });
      }
      return { preview, base64 };
    }));

    setPhotos(prev => [...prev, ...newPhotos].slice(0, 5));
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (photos.length < 3) return;
    setPhase('loading');
    setError(null);
    try {
      const data = await api.post('/analyze-attraction-photos', {
        photos: photos.map(p => p.base64),
      });
      setResult(data);
      setPhase('result');
    } catch (err) {
      setError(err.message || 'Analysis failed. Try clearer front-facing photos.');
      setPhase('upload');
    }
  };

  const handleDone = () => navigate('/onboarding/done', { replace: true });

  // ── Loading screen ────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{
        height: '100vh', background: '#000',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '0 40px', textAlign: 'center',
      }}>
        <style>{`
          @keyframes attr-pulse {
            0%   { transform: scale(0.88); opacity: 0.9; box-shadow: 0 0 0 0 rgba(201,168,76,0.4); }
            50%  { transform: scale(1.08); opacity: 0.4; box-shadow: 0 0 40px 10px rgba(201,168,76,0.1); }
            100% { transform: scale(0.88); opacity: 0.9; box-shadow: 0 0 0 0 rgba(201,168,76,0.4); }
          }
        `}</style>
        <div style={{
          width: 120, height: 120, borderRadius: '50%',
          border: '1.5px solid rgba(201,168,76,0.8)',
          animation: 'attr-pulse 2.2s ease-in-out infinite',
          marginBottom: 48,
        }} />
        <div style={{
          fontFamily: 'var(--font-display)', fontStyle: 'italic',
          fontSize: 15, color: 'rgba(255,255,255,0.45)',
          minHeight: 24,
        }}>
          {LOADING_TEXTS[loadingIdx]}
        </div>
      </div>
    );
  }

  // ── Result screen ─────────────────────────────────────────────────────────────
  if (phase === 'result' && result) {
    const { vata, pitta, kapha, dominant } = result;
    const description = getTypeDescription(vata, pitta, kapha, dominant);

    return (
      <div style={{
        minHeight: '100vh', background: '#000', color: '#fff',
        display: 'flex', flexDirection: 'column',
        padding: '80px 32px 60px',
      }}>
        <div style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: '#c9a84c', marginBottom: 28 }}>
          Pattern found.
        </div>

        <div style={{
          fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 300,
          lineHeight: 1.35, marginBottom: 36,
        }}>
          You're drawn to<br />
          <span style={{ color: '#c9a84c' }}>{description}</span>
        </div>

        {/* VPK proportion bar */}
        <div style={{ display: 'flex', height: 3, borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ flex: vata,  background: '#7c6fc2', transition: 'flex 1s ease' }} />
          <div style={{ flex: pitta, background: '#c9a84c', transition: 'flex 1s ease' }} />
          <div style={{ flex: kapha, background: '#5a8f5a', transition: 'flex 1s ease' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#333', marginBottom: 48 }}>
          <span>{vata}%</span>
          <span>{pitta}%</span>
          <span>{kapha}%</span>
        </div>

        <div style={{
          fontFamily: 'var(--font-display)', fontStyle: 'italic',
          fontSize: 14, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7,
          marginBottom: 56,
        }}>
          This shapes every recommendation we make for you.
        </div>

        <div style={{ marginTop: 'auto' }}>
          <button
            onClick={handleDone}
            style={{
              width: '100%', padding: '16px 0', borderRadius: 8, border: 'none',
              background: '#c9a84c', color: '#000',
              fontSize: 14, letterSpacing: 1, cursor: 'pointer', marginBottom: 16,
              fontWeight: 500,
            }}
          >
            See my matches →
          </button>
          <button
            onClick={handleDone}
            style={{
              width: '100%', padding: '10px 0', background: 'none', border: 'none',
              color: '#333', fontSize: 12, cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Upload screen ─────────────────────────────────────────────────────────────
  const canSubmit = photos.length >= 3;
  const remaining = 3 - photos.length;

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff', padding: '64px 24px 80px' }}>

      <div style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: '#c9a84c', marginBottom: 20 }}>
        Your Type
      </div>

      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 300,
        lineHeight: 1.3, marginBottom: 14,
      }}>
        Show us who you're drawn to.
      </div>

      <div style={{
        fontFamily: 'var(--font-display)', fontStyle: 'italic',
        fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, marginBottom: 36,
      }}>
        Upload 3–5 photos of people you find attractive.<br />
        Past partners, public figures, anyone.<br />
        We read the pattern, not the person.
      </div>

      {/* Upload zone */}
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          border: `1.5px dashed ${canSubmit ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.25)'}`,
          borderRadius: 12,
          padding: photos.length ? '28px 20px' : '56px 20px',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', marginBottom: 12,
          transition: 'border-color 0.3s',
        }}
      >
        {photos.length === 0 ? (
          <>
            <div style={{ fontSize: 32, color: 'rgba(201,168,76,0.4)', marginBottom: 10 }}>
              ⊕
            </div>
            <div style={{ fontSize: 13, color: '#444' }}>Tap to add photos</div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 14 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img
                    src={p.preview}
                    alt=""
                    style={{
                      width: 68, height: 68, borderRadius: '50%',
                      objectFit: 'cover',
                      filter: 'blur(9px) brightness(0.7)',
                      border: '1px solid rgba(201,168,76,0.3)',
                    }}
                  />
                  <button
                    onClick={e => { e.stopPropagation(); removePhoto(i); }}
                    style={{
                      position: 'absolute', top: -3, right: -3,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#222', border: '1px solid #444',
                      color: '#aaa', fontSize: 12, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <div style={{
                  width: 68, height: 68, borderRadius: '50%',
                  border: '1px dashed rgba(201,168,76,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'rgba(201,168,76,0.3)', fontSize: 22,
                }}>
                  +
                </div>
              )}
            </div>
            <div style={{
              fontSize: 22, fontFamily: 'var(--font-display)',
              color: canSubmit ? '#c9a84c' : '#555', fontWeight: 300,
            }}>
              {photos.length} / 5
            </div>
          </>
        )}
      </div>

      <div style={{
        fontSize: 11, color: '#2e2e2e', textAlign: 'center',
        lineHeight: 1.6, marginBottom: 28,
      }}>
        Photos are processed and immediately deleted.<br />
        We only keep the mathematical pattern.
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        style={{ display: 'none' }}
      />

      {error && (
        <div style={{ marginBottom: 16, fontSize: 13, color: '#c0392b', textAlign: 'center' }}>
          {error}
        </div>
      )}

      <button
        onClick={canSubmit ? handleSubmit : undefined}
        disabled={!canSubmit}
        style={{
          width: '100%', padding: '16px 0', borderRadius: 8, border: 'none',
          background: canSubmit ? '#c9a84c' : '#111',
          color: canSubmit ? '#000' : '#333',
          fontSize: 14, letterSpacing: 1,
          cursor: canSubmit ? 'pointer' : 'default',
          marginBottom: 10, transition: 'all 0.2s',
          fontWeight: canSubmit ? 500 : 400,
        }}
      >
        {canSubmit ? 'Find my pattern →' : `Add ${remaining} more photo${remaining !== 1 ? 's' : ''} to continue`}
      </button>

      {canSubmit && (
        <div style={{ textAlign: 'center', fontSize: 11, color: '#333', marginBottom: 12 }}>
          This takes about 10 seconds
        </div>
      )}

      <button
        onClick={handleDone}
        style={{
          width: '100%', padding: '12px 0', background: 'none', border: 'none',
          color: '#2a2a2a', fontSize: 12, cursor: 'pointer',
        }}
      >
        Skip this step
      </button>
    </div>
  );
}
